import React, { useState, useEffect, useRef } from "react";
import { TextField, Box, Autocomplete, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useAppBar } from "../../providers/AppBarProvider";
import { useConfig } from "../../providers/ConfigProvider";
import EditPage from "./EditPage";
import { handleError, validateNonEmpty, fetchRegions, removeNullsAndEmptys } from "../../utils/lib";
import { uploadPhotoAndThumbnail } from "../../utils/fileUploadHelper";
import { v4 as uuidv4 } from 'uuid';
import { PhotoCamera, PhotoLibrary, Delete } from "@mui/icons-material";
import useUserStore from "../../store/userStore";
import InlinePhotoCapture from "../../components/InlinePhotoCapture";
import { useTranslation } from 'react-i18next';

const EditUser = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { updateTitle, toggleFab } = useAppBar();
  const { config, isLoadingConfig, reloadConfig } = useConfig();
  const [isLoading, setIsLoading] = useState(true);

  const { updateUser: updateUserFromLocation } = location.state || {};

  const { 
    user, 
    setUser, 
    updateUser, 
    resetUser 
  } = useUserStore();

  const [regions, setRegions] = useState([]);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [uploadedImages, setUploadedImages] = useState({ original: null, avatar: null });
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [photoDeleted, setPhotoDeleted] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showInlineCamera, setShowInlineCamera] = useState(false);
  const fileInputRef = useRef(null);

  const [errors, setErrors] = useState({});
  const [refs] = useState({
    name: useRef(null),
    address: useRef(null),
    region: useRef(null),
    phone: useRef(null),
    regional_id: useRef(null),
    notes: useRef(null)
  });

  useEffect(() => {
    // Inicializar el user desde location.state si existe
    if (updateUserFromLocation) {
      setUser(updateUserFromLocation);
    }

    const canEditPhoto = config?.login_type === 'EMAIL';
    const currentThumbUrl = !photoDeleted ? (capturedPhoto?.url || user?.thumb_url || '') : '';
    const fallbackIcon = !currentThumbUrl ? PhotoCamera : null;

    updateTitle(
      currentThumbUrl,
      t('edit.editUser'),
      t('edit.editingUser'),
      false,
      fallbackIcon,
      canEditPhoto ? handleAvatarClick : null
    );

    toggleFab(false);

    const fetchData = async () => {
      try {
        const regionsData = await fetchRegions();
        setRegions(regionsData);
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);

        setTimeout(() => {
          if (refs.name.current) {
            refs.name.current.focus();
          }
        }, 100);
      }
    };
    fetchData();
  }, []);

  // Actualizar el title cuando cambie la foto capturada
  useEffect(() => {
    if (config) {
      const canEditPhoto = config.login_type === 'EMAIL';
      const currentThumbUrl = !photoDeleted ? (capturedPhoto?.url || user?.thumb_url || '') : '';
      const fallbackIcon = !currentThumbUrl ? PhotoCamera : null;

      updateTitle(
        currentThumbUrl,
        t('edit.editUser'),
        t('edit.editingUser'),
        false,
        fallbackIcon,
        canEditPhoto ? handleAvatarClick : null
      );
    }
  }, [capturedPhoto, user?.id, config, photoDeleted]);

  // Función para manejar la subida de imágenes
  const handlePhotoAndThumbUpload = async (originalFile) => {
    try {
      setIsUploadingImage(true);

      const result = await uploadPhotoAndThumbnail({
        originalFile,
        tableName: 'users',
        onError: (error) => {
          snackbar(t('common.errorUploadingImages') + error);
        }
      });

      // Actualizar estado con las imágenes subidas
      setUploadedImages({
        original: result.original,
        avatar: result.thumbnail
      });
      
      // Si se sube una nueva imagen, resetear el estado de eliminación
      setPhotoDeleted(false);
      
    } catch (error) {
      snackbar(t('common.errorUploadingImages') + (error.message || error));
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleAvatarClick = (event) => {
    // Solo permitir cambiar imagen si el usuario tiene login_type EMAIL
    if (config?.login_type === 'EMAIL') {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCameraCapture = () => {
    handleMenuClose();
    setShowInlineCamera(true);
  };

  const handleGallerySelect = () => {
    handleMenuClose();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemovePhoto = () => {
    handleMenuClose();
    // Marcar la foto como eliminada
    setPhotoDeleted(true);
    // Limpiar la foto capturada y las imágenes subidas
    setCapturedPhoto(null);
    setUploadedImages({ original: null, avatar: null });
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      // Generar un nombre de archivo único usando UUID (igual que la cámara)
      const unique_id = uuidv4();
      const extension = file.name.split('.').pop();
      const newFileName = `${unique_id}.${extension}`;
      
      // Crear un nuevo File con el nombre UUID
      const renamedFile = new File([file], newFileName, { type: file.type });
      
      const processedPhoto = {
        file: renamedFile,
        url: URL.createObjectURL(file),
        name: newFileName,
        size: file.size,
        type: file.type,
        isImage: true,
        isVideo: false,
        sizeInMB: (file.size / 1024 / 1024).toFixed(2)
      };
      
      setCapturedPhoto(processedPhoto);
      setPhotoDeleted(false);
      await handlePhotoAndThumbUpload(renamedFile);
    }
    
    // Reset input para permitir seleccionar el mismo archivo nuevamente
    event.target.value = '';
  };

  const handleInlinePhotoConfirm = async (file, previewUrl) => {
    setCapturedPhoto({
      url: previewUrl,
      file: file,
      isImage: true
    });

    setPhotoDeleted(false);
    await handlePhotoAndThumbUpload(file);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    updateUser({ [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateNonEmpty("name", user.name, setErrors, refs.name)) {
      return;
    }

    setIsLoading(true);
    
    const userToSend = { ...user };

    // Add resources array if images were uploaded
    userToSend.resources = [];
    if (uploadedImages.original && uploadedImages.avatar) {
      userToSend.resources = [
        {
          file_name: uploadedImages.original.file_name,
          table_name: "users",
          type: "PUBLIC",
          unique_id: uploadedImages.original.unique_id
        },
        {
          file_name: uploadedImages.avatar.file_name,
          table_name: "users", 
          type: "PUBLIC",
          unique_id: uploadedImages.avatar.unique_id
        }
      ];
    }

    // Mark photo as deleted if user removed it
    if (photoDeleted) {
      userToSend.photo_deleted = 1;
    }

    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}users?update&id=${user.id}`,
        removeNullsAndEmptys(userToSend),
        { withCredentials: true }
      );

      // Reload config to update user information
      await reloadConfig();

      navigate(-1);
    } catch (error) {
      handleError(error, snackbar);
      snackbar(t('common.errorSaving'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e, fieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      const fieldOrder = ['name', 'address', 'region', 'phone', 'regional_id', 'notes'];
      const currentIndex = fieldOrder.indexOf(fieldName);
      
      if (currentIndex === fieldOrder.length - 1) {
        // Last field - submit the form
        handleSubmit(e);
      } else if (currentIndex >= 0 && currentIndex < fieldOrder.length - 1) {
        // Move to the next field
        const nextField = fieldOrder[currentIndex + 1];
        if (refs[nextField] && refs[nextField].current) {
          refs[nextField].current.focus();
        }
      }
    }
  };

  const canEditPhoto = config?.login_type === 'EMAIL';

  return (
    <>
      {/* Menú de opciones de imagen (solo para usuarios con login EMAIL) */}
      {canEditPhoto && (
        <>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleCameraCapture}>
              <ListItemIcon>
                <PhotoCamera />
              </ListItemIcon>
              <ListItemText>{t('common.camera')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleGallerySelect}>
              <ListItemIcon>
                <PhotoLibrary />
              </ListItemIcon>
              <ListItemText>{t('common.gallery')}</ListItemText>
            </MenuItem>
            {(capturedPhoto || user?.photo_url || user?.thumb_url) && !photoDeleted && (
              <MenuItem onClick={handleRemovePhoto}>
                <ListItemIcon>
                  <Delete />
                </ListItemIcon>
                <ListItemText>{t('common.remove')}</ListItemText>
              </MenuItem>
            )}
          </Menu>

          {/* Input file hidden para selección de galería */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            onChange={handleFileSelect}
          />

          <InlinePhotoCapture 
            open={showInlineCamera}
            onPhotoConfirm={handleInlinePhotoConfirm}
            onClose={() => setShowInlineCamera(false)}
          />
        </>
      )}

      <EditPage 
        onSubmit={handleSubmit}
        isLoading={isLoading || isLoadingConfig || isUploadingImage}
      >
        <Box sx={{ width: "100%" }}>
        <TextField
          fullWidth
          margin="normal"
          label={t('common.nameStar')}
          name="name"
          value={user.name}
          onChange={handleChange}
          inputRef={refs.name}
          error={!!errors.name}
          helperText={errors.name}
          onKeyDown={(e) => handleKeyDown(e, 'name')}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t('common.address')}
          name="address"
          value={user.address}
          onChange={handleChange}
          inputRef={refs.address}
          onKeyDown={(e) => handleKeyDown(e, 'address')}
        />
        <Autocomplete
          fullWidth
          options={regions}
          getOptionLabel={(option) => (option ? option.friendly_name : "")}
          value={user.region}
          onChange={(e, newValue) =>
            updateUser({
              region: newValue,
            })
          }
          renderInput={(params) => (
            <TextField 
              {...params} 
              label={t('common.region')} 
              name="region" 
              margin="normal" 
              inputRef={refs.region}
              onKeyDown={(e) => handleKeyDown(e, 'region')}
            />
          )}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t('common.phone')}
          name="phone"
          value={user.phone}
          onChange={handleChange}
          inputRef={refs.phone}
          onKeyDown={(e) => handleKeyDown(e, 'phone')}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t('edit.identificationRegional')}
          name="regional_id"
          value={user.regional_id}
          onChange={handleChange}
          inputRef={refs.regional_id}
          onKeyDown={(e) => handleKeyDown(e, 'regional_id')}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t('common.notes')}
          name="notes"
          value={user.notes}
          onChange={handleChange}
          multiline
          rows={4}
          inputRef={refs.notes}
          onKeyDown={(e) => handleKeyDown(e, 'notes')}
        />
      </Box>
      </EditPage>
    </>
  );
};

export default EditUser; 
